import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Row, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { TableOutlined, SettingOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';

import WorkflowActionNode from './WorkflowActionNode';
import WorkflowHandle from './WorkflowHandle';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import useToggle from '../../common/hooks/useToggle';

import PropTypes from 'prop-types';

import {
  getType,
  updateData,
} from './workflowHelpers';
import { getScheduleOfValueTemplates } from '../../projects/state/projects.actions';
import ProjectsService from '../../projects/state/projects.service';
import WorkflowCreateContractConfigureDrawer from './WorkflowCreateContractConfigureDrawer';

const getHoverText = (t) => `
Create ${t('Project').toLowerCase()} contract SOV rows from Schedule of Values tables on this form when the step runs.
`;

const getDisabledText = (t) => `Add a ${t('Project')} dropdown and a Schedule of Values table to enable this step`;

const ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires:
      <ul style={{ paddingLeft: 16, marginTop: 8 }}>
        <li><b>Project</b> dropdown on the step</li>
        <li><b>Contract template</b> (Default or a company template)</li>
        <li><b>Configure</b>: map SOV tables for sections you want this step to fill</li>
        <li><b>Note</b>: if non-holdback items are enabled, you must map an SOV table for the non-holdback items</li>
      </ul>
    </div>
  ),
};

const emptyDefaultMappings = {};
const emptyTemplateMappings = {};

function formatProjectDropdownOption(field) {
  const { id: fieldId, configProps: { title: fieldTitle } = {} } = field;
  return { label: fieldTitle, value: fieldId };
}

function buildProjectDropdownFields(sectionsList) {
  return sectionsList?.flatMap(({ fields = [] }) =>
    (fields ?? [])
      .filter((field) => getType(field) === 'dropdown - Projects')
      .map(formatProjectDropdownOption)
  );
}

export default function WorkflowCreateContractNode({
  setElements,
  setDataMap,
  isDisplay,
  sections = [],
  divisionId,
  setDisableNext,
} = {}) {
  function Inner({
    id,
    draggable,
    data = {},
    disabled,
  }) {
    const { t } = useTranslation();
    const HOVER_TEXT = getHoverText(t);
    const DISABLED_TEXT = getDisabledText(t);
    const dispatch = useDispatch();
    const scheduleOfValueTemplates = useSelector((state) => state.projects.scheduleOfValueTemplates);
    const selectedDivisions = useSelector((state) => state.settings.selectedDivisions);

    const {
      projectFieldId: initialProjectFieldId,
      scheduleOfValueTemplateId: initialTemplateId,
      defaultSectionMappings: initialDefaultMappings = emptyDefaultMappings,
      templateSectionMappings: initialTemplateMappings = emptyTemplateMappings,
      includeNonHoldback: initialIncludeNonHoldback = false,
    } = data;

    const title = `Create Contract${disabled ? ' - DISABLED' : ''}`;

    const [projectFieldId, setProjectFieldId] = useState(initialProjectFieldId);
    const [scheduleOfValueTemplateId, setScheduleOfValueTemplateId] = useState(initialTemplateId);
    const [defaultSectionMappings, setDefaultSectionMappings] = useState(
      () => ({ ...initialDefaultMappings }),
    );
    const [templateSectionMappings, setTemplateSectionMappings] = useState(
      () => ({ ...initialTemplateMappings }),
    );
    const [includeNonHoldback, setIncludeNonHoldback] = useState(
      initialIncludeNonHoldback || !!initialDefaultMappings?.nonHoldback,
    );
    const [templateSections, setTemplateSections] = useState([]);
    const [loadingTemplateSections, setLoadingTemplateSections] = useState(false);

    const { isToggled: configureVisible, toggle: toggleConfigure } = useToggle();

    useEffect(() => {
      dispatch(getScheduleOfValueTemplates());
    }, []);

    useEffect(() => {
      setProjectFieldId(initialProjectFieldId);
      setScheduleOfValueTemplateId(initialTemplateId);
      setDefaultSectionMappings({ ...initialDefaultMappings });
      setTemplateSectionMappings({ ...initialTemplateMappings });
      setIncludeNonHoldback(initialIncludeNonHoldback || !!initialDefaultMappings.nonHoldback);
    }, [
      initialProjectFieldId, initialTemplateId, initialDefaultMappings, initialTemplateMappings,
      initialIncludeNonHoldback,
    ]);

    const isDefaultTemplate = scheduleOfValueTemplateId === null;

    useEffect(() => {
      if (!scheduleOfValueTemplateId || isDefaultTemplate) {
        setTemplateSections([]);
        setLoadingTemplateSections(false);
        return;
      }
      let cancelled = false;
      (async () => {
        setLoadingTemplateSections(true);
        const res = await ProjectsService.getScheduleOfValueSections({
          templateId: scheduleOfValueTemplateId,
        });
        if (!cancelled) {
          setTemplateSections(Array.isArray(res?.data) ? res.data : []);
          setLoadingTemplateSections(false);
        }
      })();
      return () => { cancelled = true; };
    }, [scheduleOfValueTemplateId, isDefaultTemplate]);

    const projectDropdownFields = useMemo(
      () => buildProjectDropdownFields(sections),
      [sections],
    );

    const templateOptions = useMemo(() => {
      const customTemplates = scheduleOfValueTemplates
        .filter((template) => !divisionId || template.divisionId === divisionId)
        .filter((template) => !selectedDivisions?.size || selectedDivisions.has(template.divisionId))
        .map((template) => ({ label: template.name, value: template.id }));
      return [
        { label: 'Default', value: null },
        ...customTemplates,
      ];
    }, [scheduleOfValueTemplates, divisionId, selectedDivisions]);

    const disableNext = useMemo(() => (
      !projectFieldId || scheduleOfValueTemplateId === undefined
      || (
        Object.keys(defaultSectionMappings).length === 0
        && Object.keys(templateSectionMappings).length === 0
        && !includeNonHoldback
      )
      || (includeNonHoldback && !defaultSectionMappings.nonHoldback)
    ), [
      projectFieldId, scheduleOfValueTemplateId, defaultSectionMappings, templateSectionMappings,
      includeNonHoldback,
    ]);

    useEffect(() => {
      if (!setDisableNext) return;
      setDisableNext((prev) => ({ ...prev, [id]: disableNext }));
    }, [disableNext, setDisableNext, id]);

    const persistPartial = useCallback((patch) => {
      if (!setDataMap || !id) return;
      const newData = {
        projectFieldId,
        scheduleOfValueTemplateId,
        defaultSectionMappings,
        templateSectionMappings,
        includeNonHoldback,
        ...patch,
      };
      setProjectFieldId(newData.projectFieldId);
      setScheduleOfValueTemplateId(newData.scheduleOfValueTemplateId);
      setDefaultSectionMappings(newData.defaultSectionMappings);
      setTemplateSectionMappings(newData.templateSectionMappings);
      setIncludeNonHoldback(newData.includeNonHoldback ?? false);
      setDataMap(updateData(id, newData));
    }, [
      setDataMap, id, projectFieldId, scheduleOfValueTemplateId,
      defaultSectionMappings, templateSectionMappings, includeNonHoldback,
    ]);

    const onProjectChange = useCallback((value) => {
      persistPartial({ projectFieldId: value });
    }, [persistPartial]);

    const onTemplateChange = useCallback((value) => {
      persistPartial({
        scheduleOfValueTemplateId: value,
        defaultSectionMappings: {},
        templateSectionMappings: {},
        includeNonHoldback: false,
      });
    }, [persistPartial]);

    const onMappingChange = useCallback((mapping) => {
      persistPartial(mapping);
    }, [persistPartial]);

    return (
      <WorkflowActionNode
        id={id}
        title={title}
        Icon={TableOutlined}
        type="createContract"
        draggable={draggable}
        onNodeUpdate={(...args) => {
          setElements(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        isDisplay={isDisplay || disabled}
        hover={disabled ? DISABLED_TEXT : HOVER_TEXT}
        style={disabled ? { opacity: 0.7 } : {}}
        errorPopover={disableNext ? ERROR_DETAILS : undefined}
      >
        {!draggable && (
          <div>
            <Row style={{ marginTop: 10 }}>
              {t('Project')}
              {' '}
              dropdown:
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="label"
                placeholder={`Select ${t('Project')} field`}
                value={projectFieldId}
                onChange={onProjectChange}
                options={projectDropdownFields}
              />
            </Row>
            <Row style={{ marginTop: 10 }}>
              Contract template:
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="label"
                placeholder="Select template"
                value={scheduleOfValueTemplateId === undefined ? undefined : scheduleOfValueTemplateId}
                onChange={onTemplateChange}
                options={templateOptions}
              />
            </Row>
            <Row style={{ marginTop: 10 }}>
              <BorderlessButton
                title="Configure"
                style={{ margin: 5 }}
                iconNode={<SettingOutlined />}
                onClick={toggleConfigure}
              />
            </Row>
            <WorkflowCreateContractConfigureDrawer
              key={String(configureVisible)}
              visible={configureVisible}
              onClose={toggleConfigure}
              onSubmit={toggleConfigure}
              sections={sections}
              isDefaultTemplate={isDefaultTemplate}
              scheduleOfValueTemplateId={scheduleOfValueTemplateId}
              templateSections={templateSections}
              loadingTemplateSections={loadingTemplateSections}
              defaultSectionMappings={defaultSectionMappings}
              templateSectionMappings={templateSectionMappings}
              includeNonHoldback={includeNonHoldback}
              onMappingChange={onMappingChange}
            />
            <WorkflowHandle type="target" position="top" disabled={isDisplay} />
          </div>
        )}
      </WorkflowActionNode>
    );
  }

  Inner.propTypes = {
    id: PropTypes.string.isRequired,
    draggable: PropTypes.bool.isRequired,
    data: PropTypes.shape({
      projectFieldId: PropTypes.string,
      scheduleOfValueTemplateId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.oneOf([null]),
      ]),
      defaultSectionMappings: PropTypes.objectOf(PropTypes.string),
      templateSectionMappings: PropTypes.objectOf(PropTypes.string),
      includeNonHoldback: PropTypes.bool,
    }),
    disabled: PropTypes.bool,
  };

  return Inner;
}

WorkflowCreateContractNode.propTypes = {
  setElements: PropTypes.func.isRequired,
  setDataMap: PropTypes.func.isRequired,
  isDisplay: PropTypes.bool.isRequired,
  sections: PropTypes.array.isRequired,
  divisionId: PropTypes.string.isRequired,
  setDisableNext: PropTypes.func.isRequired,
};

WorkflowCreateContractNode.defaultProps = {
  sections: [],
};